// src/utils/staffSession.js
const SESSION_KEYS = [
  "staffSession",
  "cashup_staff_session",
  "session_staff",
  "cashup.session",
];

function readJSON(source, key) {
  try {
    const raw = source?.getItem?.(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function normalizeStaffSession(input) {
  if (!input || typeof input !== "object") return null;

  const name = String(
    input.name || input.staffName || localStorage.getItem("staffName") || ""
  ).trim();
  const role = String(
    input.role || localStorage.getItem("staffRole") || ""
  ).trim();
  const orgId = String(
    input.orgId || localStorage.getItem("orgId") || ""
  ).trim();
  const storeId = String(
    input.storeId || localStorage.getItem("storeId") || ""
  ).trim();
  const storeName = String(
    input.storeName ||
      localStorage.getItem("storeName") ||
      localStorage.getItem("storeLabel") ||
      ""
  ).trim();

  if (!name || !storeId) return null;

  const startedAt = Number(input.startedAt || input.setAt || Date.now());
  const expiresAt = Number(input.expiresAt || startedAt + 16 * 60 * 60 * 1000);

  return {
    ...input,
    type: "staff",
    name,
    role,
    orgId,
    storeId,
    storeName,
    startedAt,
    expiresAt,
  };
}

function persistStaffSession(session) {
  if (!session) return null;
  const raw = JSON.stringify(session);

  try {
    sessionStorage.setItem("staffSession", raw);
  } catch {}

  try {
    SESSION_KEYS.forEach((key) => localStorage.setItem(key, raw));
    if (session.name) localStorage.setItem("staffName", session.name);
    if (session.role) localStorage.setItem("staffRole", session.role);

    // IMPORTANT: a staff session is subordinate to the linked kiosk binding.
    // Never let a stale staff session rewrite orgId/storeId/storeName.
    // Those keys belong to storeBinding / LinkDevice only.
  } catch {}

  return session;
}

export function setStaffSession(payload = {}) {
  const session = normalizeStaffSession(payload);
  return session ? persistStaffSession(session) : null;
}

export function getStaffSession() {
  const boundOrgId = String(localStorage.getItem("orgId") || "").trim();
  const boundStoreId = String(localStorage.getItem("storeId") || "").trim();

  const isForCurrentBinding = (session) => {
    if (!session) return false;
    const sessionOrgId = String(session.orgId || "").trim();
    const sessionStoreId = String(session.storeId || "").trim();

    if (boundOrgId && sessionOrgId && boundOrgId !== sessionOrgId) return false;
    if (boundStoreId && sessionStoreId && boundStoreId !== sessionStoreId) return false;
    return true;
  };

  const sessionCandidate = normalizeStaffSession(readJSON(sessionStorage, "staffSession"));
  if (sessionCandidate && isForCurrentBinding(sessionCandidate)) {
    return persistStaffSession(sessionCandidate);
  }

  for (const key of SESSION_KEYS) {
    const found = normalizeStaffSession(readJSON(localStorage, key));
    if (found && isForCurrentBinding(found)) {
      return persistStaffSession(found);
    }
  }

  // If an old store's session survived a relink, remove it instead of allowing
  // it to silently switch the kiosk back to the previous store.
  clearStaffSession();

  const legacy = normalizeStaffSession({
    name: localStorage.getItem("staffName") || "",
    role: localStorage.getItem("staffRole") || "",
    orgId: boundOrgId,
    storeId: boundStoreId,
    storeName:
      localStorage.getItem("storeName") ||
      localStorage.getItem("storeLabel") ||
      "",
  });

  return legacy && isForCurrentBinding(legacy)
    ? persistStaffSession(legacy)
    : null;
}

export function ensureStaffSession(seed = {}) {
  const normalizedSeed = normalizeStaffSession(seed);
  if (normalizedSeed) return persistStaffSession(normalizedSeed);
  const existing = getStaffSession();
  if (existing) return existing;
  return setStaffSession(seed);
}

export function clearStaffSession() {
  try {
    sessionStorage.removeItem("staffSession");
  } catch {}

  try {
    SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
    ["staffName", "cashup_staff_name", "staffRole"].forEach((key) =>
      localStorage.removeItem(key)
    );
  } catch {}
}
